import React from 'react';
import clsx from 'clsx';
import { Button, colors, Dropdown } from '@insurancemenu/imenu-components-lib';
import { DropdownOptionType, OptionsAlignmentTypes } from '@insurancemenu/imenu-components-lib/dist/types/Dropdown';
import {
  Bookmark,
  ChevronDown,
  Clipboard,
  Delete,
  DriveFile,
  Edit,
  Star,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import { useRootStore } from 'store/rootStore';
import { observer } from 'mobx-react-lite';
import { EnrollmentPeriod, Proposal, RfqProposalStatus } from 'types/enrollmentService';
import ActionModal from 'components/common/modals/ActionModal';
import { createStyles } from './styles';

interface Props {
  cartId: string;
  enrollmentPeriod: EnrollmentPeriod;
  proposal: Proposal;
  redirectToProposalPage: () => void;
}

const MoreDropdown: React.FC<Props> = observer(
  ({
    cartId,
    enrollmentPeriod: { id: enrollmentPeriodId, hasConfirmedProposal },
    proposal,
    redirectToProposalPage,
  }) => {
    const { groupProposalsTableStore, updateProposalModalStore } = useRootStore();
    const { name: proposalName, confirmed: proposalConfirmed, rateSegment } = proposal;
    const [showFailingModal, setShowFailingModal] = React.useState(false);

    const classes = createStyles();

    // If proposal confirmed don't show the delete proposal option.
    function buildOptions(): DropdownOptionType[] {
      const ViewProposalOption: DropdownOptionType = {
        value: 'viewProposal',
        label: (
          <Button type="text" leftIconProp={<DriveFile />}>
            View Proposal
          </Button>
        ),
        onOptionClick: (): void => {
          redirectToProposalPage();
        },
        className: classes.dropdownOption,
      };

      const EditProposalOption: DropdownOptionType = {
        value: 'editProposal',
        label: (
          <Button type="text" leftIconProp={<Edit />}>
            Edit Proposal
          </Button>
        ),
        onOptionClick: async (): Promise<void> => {
          await groupProposalsTableStore.editProposalV2(cartId);
        },
        className: classes.dropdownOption,
      };
      const RenameProposalOption: DropdownOptionType = {
        value: 'renameProposal',
        label: (
          <Button type="text" leftIconProp={<Clipboard />}>
            Rename Proposal
          </Button>
        ),
        onOptionClick: (): void => {
          updateProposalModalStore.openRenameProposalModal(proposalName, cartId, enrollmentPeriodId);
        },
        className: classes.dropdownOption,
      };
      const MarkAsFavoriteOption: DropdownOptionType = {
        value: 'markAsFavorite',
        label: (
          <Button type="text" leftIconProp={<Star />}>
            Mark as Favorite
          </Button>
        ),
        onOptionClick: (): void => {
          updateProposalModalStore.openSaveProposalModal(proposalName, cartId, rateSegment);
        },
        className: classes.dropdownOption,
      };
      const MarkAsPreferredOption: DropdownOptionType = {
        value: 'markAsPreferred',
        label: (
          <Button type="text" leftIconProp={<Bookmark />}>
            Propose Quote
          </Button>
        ),
        onOptionClick: (): void => {
          groupProposalsTableStore.openConfirmationModal('MARK_AS_PREFERRED', { enrollmentPeriodId, cartId });
        },
        className: classes.dropdownOption,
      };
      const DeleteProposalOption: DropdownOptionType = {
        value: 'deleteProposal',
        label: (
          <Button type="text" leftIconProp={<Delete />} color="custom" customColor={colors.red[600]}>
            Delete Proposal
          </Button>
        ),
        onOptionClick: (): void => {
          groupProposalsTableStore.openConfirmationModal('DELETE_PROPOSAL', { enrollmentPeriodId, cartId });
        },
        className: clsx(classes.dropdownOption, classes.deleteOption),
      };

      const options: DropdownOptionType[] = [ViewProposalOption];

      if (proposal?.status && proposal.status !== RfqProposalStatus.RELEASED) {
        options.push(DeleteProposalOption);
        return options;
      }
      if (groupProposalsTableStore.isEditProposalAvailable(cartId)) {
        options.push(EditProposalOption);
      }

      options.push(RenameProposalOption, MarkAsFavoriteOption);
      if (!hasConfirmedProposal) options.push(MarkAsPreferredOption);
      if (!proposalConfirmed) {
        options.push(DeleteProposalOption);
      }
      return options;
    }

    return (
      <>
        <Dropdown
          name="More"
          options={buildOptions()}
          optionsAlignment={OptionsAlignmentTypes.BOTTOM_LEFT}
          optionsWidth="22rem"
          closedElement={
            <Button
              className={clsx(classes.dropdownOpenedBtn, classes.dropdownButton)}
              color="primary"
              rightIconProp={<ChevronDown />}
            >
              More
            </Button>
          }
          openedElement={
            <Button color="primary" className={classes.dropdownButton} rightIconProp={<ChevronDown />}>
              More
            </Button>
          }
        />
        <ActionModal
          show={showFailingModal}
          onClose={(): void => {
            setShowFailingModal(false);
          }}
          title="Something went wrong during generating the proposal, please try again"
          subtitle={
            <p>
              If you still have issues, please send an email to <br />
              <a href="mailto:support@insurancemenu.com">support@insurancemenu.com</a>
            </p>
          }
        />
      </>
    );
  },
);

export default MoreDropdown;
